import React from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TextMaskCustom from '@components/common/dynamicFormFields/mask/TextMaskCustom';
import IntegerMask from '@components/common/dynamicFormFields/mask/IntegerMask';
import DecimalMask from '@components/common/dynamicFormFields/mask/DecimalMask';
import { FormFieldType, TextFieldType } from '@/type';
import { StringSchema } from 'yup';
import { buildFormFieldLabel } from '@/util';
import { INTEGER_MAX, INTEGER_MIN } from '@/constant';

interface TextFieldsProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  textField: TextFieldType;
}

const TextFieldController = <TFieldValues extends FieldValues = FieldValues>({
  control,
  textField,
}: TextFieldsProps<TFieldValues>) => {
  const { t } = useTranslation();

  const buildMask = (inputProps) => {
    switch (textField.type) {
      case FormFieldType.Number:
        return {
          inputComponent: IntegerMask as React.ComponentType<any>,
          inputProps,
        };
      case FormFieldType.FloatNumber:
        return {
          inputComponent: DecimalMask as React.ComponentType<any>,
          inputProps,
        };
      default:
        if (textField.mask) {
          return {
            inputComponent: TextMaskCustom as React.ComponentType<any>,
            inputProps,
          };
        }
        return undefined;
    }
  };

  const getStepValue = (type: FormFieldType) => {
    return type === FormFieldType.FloatNumber ? 0.1 : 1;
  };

  const handleStep = (
    currentValue: string,
    isIncrement: boolean,
    type: FormFieldType,
  ) => {
    const step = getStepValue(type);
    const newValue = isIncrement
      ? Number(currentValue) + step
      : Number(currentValue) - step;

    if (type === FormFieldType.Number) {
      return Math.min(Math.max(newValue, INTEGER_MIN), INTEGER_MAX).toString();
    }
    return Math.min(Math.max(newValue, INTEGER_MIN), INTEGER_MAX).toString();
  };

  const buildLabel = (fieldValue: string) => {
    const baseLabel = buildFormFieldLabel(
      t(textField.label),
      textField.validation as StringSchema,
    );
    const maxLength = textField.inputProps?.maxLength;

    if (!maxLength) {
      return baseLabel;
    }

    return `${baseLabel} (${fieldValue?.length ?? 0}/${maxLength})`;
  };

  return (
    <Controller
      name={textField.name as Path<TFieldValues>}
      control={control}
      render={({ field, fieldState }) => {
        const { error } = fieldState;

        const inputProps = {
          mask: textField.mask,
          name: field.name,
          onChange: field.onChange,
          inputRef: field.ref,
        };

        const handleStepUp = () => {
          const newValue = handleStep(field.value, true, textField.type);
          field.onChange(newValue);
          field.onBlur();
          textField?.onBlur?.(field.name);
        };

        const handleStepDown = () => {
          const newValue = handleStep(field.value, false, textField.type);
          field.onChange(newValue);
          field.onBlur();
          textField?.onBlur?.(field.name);
        };
        const showStepper = [FormFieldType.Number, FormFieldType.FloatNumber].includes(
          textField.type,
        );

        return (
          <TextField
            id={textField.name}
            {...field}
            label={buildLabel(field.value)}
            placeholder={textField.placeholder}
            fullWidth
            variant='outlined'
            disabled={textField.disabled}
            multiline={textField.isMultiRow || false}
            rows={textField.isMultiRow ? 3 : undefined}
            error={!!error}
            helperText={error ? t(error.message || '') : ''}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={(event) => {
              const value = event.target.value;
              field.onChange(value);
              textField?.onChange?.(value);
            }}
            inputProps={textField.inputProps}
            InputProps={{
              startAdornment: textField.preFix ? (
                <InputAdornment position='start'>{textField.preFix}</InputAdornment>
              ) : null,
              endAdornment: (
                <>
                  {textField.postFix && (
                    <InputAdornment position='end'>{textField.postFix}</InputAdornment>
                  )}
                  {showStepper && (
                    <InputAdornment position='end'>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <IconButton size='small' onClick={handleStepUp}>
                          <ArrowDropUpIcon fontSize='small' />
                        </IconButton>
                        <IconButton size='small' onClick={handleStepDown}>
                          <ArrowDropDownIcon fontSize='small' />
                        </IconButton>
                      </div>
                    </InputAdornment>
                  )}
                </>
              ),
              ...buildMask(inputProps),
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                field.onBlur();
                textField?.onBlur?.(field.name);
              }
            }}
            onBlur={() => {
              field.onBlur();
              textField?.onBlur?.(field.name);
            }}
          />
        );
      }}
    />
  );
};

export default TextFieldController;
